import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Replay as RerunIcon,
  Stop as StopIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { RUN_STATUS } from '../utils/constants';
import DataPreview from '../components/pipeline/DataPreview';

// Status chip colors
const statusColors = {
  [RUN_STATUS.PENDING]: 'default',
  [RUN_STATUS.RUNNING]: 'info',
  [RUN_STATUS.COMPLETED]: 'success',
  [RUN_STATUS.FAILED]: 'error',
  [RUN_STATUS.CANCELLED]: 'warning',
};

const PipelineRunsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const pipelineIdFromQuery = queryParams.get('pipeline_id');
  
  // States
  const [runs, setRuns] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);
  const [isRunDetailsOpen, setIsRunDetailsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterPipelineId, setFilterPipelineId] = useState(pipelineIdFromQuery || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [runDetails, setRunDetails] = useState(null);
  const [runLogs, setRunLogs] = useState(null);
  const [isRunDetailsLoading, setIsRunDetailsLoading] = useState(false);

  // Refs for polling
  const runsRef = useRef(runs);
  runsRef.current = runs;
  const selectedRunRef = useRef(selectedRun);
  selectedRunRef.current = selectedRun;

  // Helper to build runs URL with current filters
  const buildRunsUrl = useCallback(() => {
    let runsUrl = '/runs/';
    const params = new URLSearchParams();
    if (filterPipelineId) params.append('pipeline_id', filterPipelineId);
    if (filterStatus) params.append('status', filterStatus);
    if (searchQuery) params.append('search', searchQuery);
    if (params.toString()) runsUrl += `?${params.toString()}`;
    return runsUrl;
  }, [filterPipelineId, filterStatus, searchQuery]);

  // Fetch runs list (used for both initial load and polling)
  const fetchRuns = useCallback(async () => {
    try {
      const runsResponse = await api.get(buildRunsUrl());
      setRuns(runsResponse.data);
    } catch (error) {
      console.error('Error fetching runs:', error);
    }
  }, [buildRunsUrl]);

  // Fetch runs and pipelines on mount / filter change
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const pipelinesResponse = await api.get('/pipelines/');
        setPipelines(pipelinesResponse.data);
        await fetchRuns();
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading data',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fetchRuns]);

  // Poll runs list every 5s when any run is pending/running
  useEffect(() => {
    const hasActiveRuns = () =>
      runsRef.current.some(
        (r) => r.status === RUN_STATUS.PENDING || r.status === RUN_STATUS.RUNNING
      );

    if (!hasActiveRuns()) return;

    const interval = setInterval(() => {
      if (hasActiveRuns()) {
        fetchRuns();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [runs, fetchRuns]);

  // Poll run details + logs every 3s when dialog is open and run is active
  useEffect(() => {
    if (!isRunDetailsOpen || !selectedRun) return;
    const isActive =
      selectedRun.status === RUN_STATUS.PENDING ||
      selectedRun.status === RUN_STATUS.RUNNING;
    if (!isActive) return;

    const pollDetails = async () => {
      try {
        const [detailsRes, logsRes] = await Promise.all([
          api.get(`/runs/${selectedRunRef.current.id}`),
          api.get(`/runs/${selectedRunRef.current.id}/logs`),
        ]);
        setRunDetails(detailsRes.data);
        setRunLogs(logsRes.data.logs || []);
        // Update selectedRun status so polling stops when terminal
        setSelectedRun((prev) =>
          prev ? { ...prev, status: detailsRes.data.status } : prev
        );
      } catch (error) {
        console.error('Error polling run details:', error);
      }
    };

    const interval = setInterval(pollDetails, 3000);
    return () => clearInterval(interval);
  }, [isRunDetailsOpen, selectedRun]);
  
  // Get pipeline name by ID
  const getPipelineName = (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    return pipeline ? pipeline.name : 'Unknown Pipeline';
  };
  
  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end - start;
    
    const seconds = Math.floor(durationMs / 1000) % 60;
    const minutes = Math.floor(durationMs / (1000 * 60)) % 60;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };
  
  // Handle run details view
  const handleViewRunDetails = async (run) => {
    setSelectedRun(run);
    setIsRunDetailsOpen(true);
    setIsRunDetailsLoading(true);
    setRunLogs(null);

    try {
      // Fetch detailed run information and logs in parallel
      const [detailsRes, logsRes] = await Promise.all([
        api.get(`/runs/${run.id}`),
        api.get(`/runs/${run.id}/logs`),
      ]);
      setRunDetails(detailsRes.data);
      setRunLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching run details:', error);
      setSnackbar({
        open: true,
        message: 'Error loading run details',
        severity: 'error',
      });
    } finally {
      setIsRunDetailsLoading(false);
    }
  };
  
  // Handle run cancellation
  const handleCancelRun = async (run) => {
    try {
      await api.post(`/runs/${run.id}/cancel`, {});
      
      // Update run status in the list
      setRuns(runs.map(r => {
        if (r.id === run.id) {
          return { ...r, status: RUN_STATUS.CANCELLED };
        }
        return r;
      }));
      
      setSnackbar({
        open: true,
        message: 'Run cancelled successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error cancelling run:', error);
      setSnackbar({
        open: true,
        message: 'Error cancelling run',
        severity: 'error',
      });
    }
  };
  
  // Handle run deletion
  const handleDeleteRun = async () => {
    if (!selectedRun) return;
    
    try {
      await api.delete(`/runs/${selectedRun.id}`);
      
      setRuns(prev => prev.filter(r => r.id !== selectedRun.id));
      fetchRuns();
      setSnackbar({
        open: true,
        message: 'Run deleted successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting run:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error deleting run',
        severity: 'error',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedRun(null);
    }
  };
  
  // Handle re-running a pipeline
  const handleRerunPipeline = async (run) => {
    try {
      await api.post('/runs/', { pipeline_id: run.pipeline_id });

      setSnackbar({
        open: true,
        message: 'Pipeline started successfully',
        severity: 'success',
      });

      // Refresh runs
      const response = await api.get('/runs/');
      setRuns(response.data);
    } catch (error) {
      console.error('Error running pipeline:', error);
      setSnackbar({
        open: true,
        message: 'Error starting pipeline',
        severity: 'error',
      });
    }
  };
  
  // Handle filter reset
  const handleResetFilters = () => {
    setFilterPipelineId('');
    setFilterStatus('');
    setSearchQuery('');
    
    // Clear query params
    navigate('/runs');
  };
  
  // Render run details tabs
  const renderRunDetailsTabs = () => {
    if (isRunDetailsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (!runDetails) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No details available for this run.
          </Typography>
        </Box>
      );
    }
    
    switch (activeTab) {
      case 0: // Overview
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Run Information
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: '30%', fontWeight: 'bold' }}>
                      Run ID
                    </TableCell>
                    <TableCell>{runDetails.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Pipeline
                    </TableCell>
                    <TableCell>{getPipelineName(runDetails.pipeline_id)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Status
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={runDetails.status} 
                        size="small" 
                        color={statusColors[runDetails.status]}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Started At
                    </TableCell>
                    <TableCell>
                      {runDetails.started_at 
                        ? new Date(runDetails.started_at).toLocaleString() 
                        : 'Not started'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Completed At
                    </TableCell>
                    <TableCell>
                      {runDetails.completed_at 
                        ? new Date(runDetails.completed_at).toLocaleString() 
                        : 'Not completed'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Duration
                    </TableCell>
                    <TableCell>
                      {formatDuration(runDetails.started_at, runDetails.completed_at)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Triggered By
                    </TableCell>
                    <TableCell>{runDetails.triggered_by || 'System'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Trigger Type
                    </TableCell>
                    <TableCell>{runDetails.trigger_type || 'N/A'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {runDetails.error_message && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom color="error">
                  Error Information
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'error.lightest',
                    border: '1px solid',
                    borderColor: 'error.light',
                  }}
                >
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {runDetails.error_message}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        );
        
      case 1: { // Node Execution
        const runPipeline = pipelines.find((p) => p.id === runDetails.pipeline_id);
        const storageOff = runPipeline?.config?.store_node_data === false;
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Node Execution Details
            </Typography>

            {runDetails.node_runs && runDetails.node_runs.length > 0 ? (
              runDetails.node_runs.map((node, index) => (
                <Accordion key={node.id || index} sx={{ mb: 1 }} TransitionProps={{ unmountOnExit: true }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 'medium' }} noWrap>
                          {node.node_name || `Node ${node.node_id}`}
                        </Typography>
                        {node.module_name && (
                          <Typography variant="caption" color="text.secondary">
                            {node.module_name}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={node.status}
                        size="small"
                        color={statusColors[node.status]}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90, textAlign: 'right' }}>
                        {`${node.input_record_count ?? 0} → ${node.output_record_count ?? 0} records`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right', mr: 1 }}>
                        {formatDuration(node.started_at, node.completed_at)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                        {`Started ${node.started_at ? new Date(node.started_at).toLocaleString() : '—'}`}
                        {` · Completed ${node.completed_at ? new Date(node.completed_at).toLocaleString() : '—'}`}
                        {` · ${node.processing_time_ms ?? 0} ms`}
                      </Typography>

                      {node.error_message && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: 'error.lightest',
                            border: '1px solid',
                            borderColor: 'error.light',
                            maxHeight: 240,
                            overflow: 'auto',
                          }}
                        >
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                            {node.error_message}
                          </Typography>
                        </Paper>
                      )}

                      <DataPreview
                        runId={runDetails.id}
                        nodeId={node.node_id}
                        nodeType={node.node_type}
                        status={node.status}
                        hasData={Boolean(node.has_data)}
                        storageOff={storageOff}
                      />

                      {node.metrics && Object.keys(node.metrics).length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Performance Metrics
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableBody>
                                {Object.entries(node.metrics).map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                      {key}
                                    </TableCell>
                                    <TableCell>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No node execution details available.
              </Typography>
            )}
          </Box>
        );
      }
        
      case 2: // Logs
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Run Logs
            </Typography>

            {runLogs && runLogs.length > 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: '500px',
                  overflow: 'auto',
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  fontFamily: 'monospace',
                }}
              >
                {runLogs.map((log, index) => (
                  <Box key={index} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" component="pre" sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      m: 0,
                      color: log.level === 'ERROR' ? '#f44336' : 'inherit',
                    }}>
                      {`[${log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}] [${log.level}]${log.node ? ` [${log.node}]` : ''} ${log.message}`}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No logs available for this run.
              </Typography>
            )}
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pipeline Runs</Typography>
        
        {filterPipelineId && (
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/pipelines/${filterPipelineId}`)}
          >
            Back to Pipeline
          </Button>
        )}
      </Box>
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Pipeline</InputLabel>
            <Select
              value={filterPipelineId}
              onChange={(e) => {
                setFilterPipelineId(e.target.value);
                if (e.target.value) {
                  navigate(`/runs?pipeline_id=${e.target.value}`);
                }
              }}
              label="Filter by Pipeline"
              size="small"
            >
              <MenuItem value="">All Pipelines</MenuItem>
              {pipelines.map((pipeline) => (
                <MenuItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={RUN_STATUS.PENDING}>Pending</MenuItem>
              <MenuItem value={RUN_STATUS.RUNNING}>Running</MenuItem>
              <MenuItem value={RUN_STATUS.COMPLETED}>Completed</MenuItem>
              <MenuItem value={RUN_STATUS.FAILED}>Failed</MenuItem>
              <MenuItem value={RUN_STATUS.CANCELLED}>Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            placeholder="Search runs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleResetFilters}
            size="small"
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Runs Table */}
      {runs.length > 0 ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id} hover>
                  <TableCell component="th" scope="row">
                    {run.id}
                  </TableCell>
                  <TableCell>{getPipelineName(run.pipeline_id)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={run.status} 
                      size="small" 
                      color={statusColors[run.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {run.started_at
                      ? new Date(run.started_at).toLocaleString()
                      : 'Not started'}
                  </TableCell>
                  <TableCell>
                    {run.completed_at
                      ? new Date(run.completed_at).toLocaleString()
                      : 'Not completed'}
                  </TableCell>
                  <TableCell>
                    {formatDuration(run.started_at, run.completed_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewRunDetails(run)}
                          sx={{ mr: 1 }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {run.status === RUN_STATUS.RUNNING && (
                        <Tooltip title="Cancel Run">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCancelRun(run)}
                            sx={{ mr: 1 }}
                          >
                            <StopIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Rerun Pipeline">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRerunPipeline(run)}
                          sx={{ mr: 1 }}
                        >
                          <RerunIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete Run">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedRun(run);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            py: 8,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <FilterIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No runs found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph align="center">
            {filterPipelineId || filterStatus || searchQuery
              ? 'No runs match your current filters'
              : 'No pipeline runs have been executed yet'}
          </Typography>
          {(filterPipelineId || filterStatus || searchQuery) && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      )}
      
      {/* Run Details Dialog */}
      <Dialog
        open={isRunDetailsOpen}
        onClose={() => setIsRunDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Run Details: {selectedRun?.id} 
              {selectedRun && (
                <Chip 
                  label={selectedRun.status} 
                  size="small" 
                  color={statusColors[selectedRun.status]}
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <IconButton onClick={() => setIsRunDetailsOpen(false)}>
              <ClearIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ px: 2, pt: 1 }}
        >
          <Tab label="Overview" />
          <Tab label="Node Execution" />
          <Tab label="Logs" />
        </Tabs>
        
        <Divider />
        
        <DialogContent sx={{ p: 0 }}>
          {renderRunDetailsTabs()}
        </DialogContent>
        
        <Divider />
        
        <DialogActions>
          {selectedRun && selectedRun.status === RUN_STATUS.RUNNING && (
            <Button
              startIcon={<StopIcon />}
              color="warning"
              onClick={() => handleCancelRun(selectedRun)}
            >
              Cancel Run
            </Button>
          )}
          
          {selectedRun && (
            <Button
              startIcon={<RerunIcon />}
              color="primary"
              onClick={() => handleRerunPipeline(selectedRun)}
            >
              Rerun Pipeline
            </Button>
          )}
          
          <Button onClick={() => setIsRunDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Run</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete run #{selectedRun?.id}?
            This action cannot be undone and all execution records will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRun} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PipelineRunsPage;
